import { fieldTypes } from "@/components/securitySelfAuditTemplateBuilder/config";

export default class SimulacrumFormNormalizer {
  constructor(vm, templateFields, options = {}) {
    this.vm = vm;
    this.templateFields = templateFields || [];
    this.options = options;
    this.customComponents = options.customComponents || {};
    this.model = options.model || {};
    this.currentState = options.currentState || "in_progress";
    this.isReviewer = !!options.isReviewer;
    this.onUpdateFiles = options.onUpdateFiles || null;
    this.onUpdateComments = options.onUpdateComments || null;
    this.onAddComment = options.onAddComment || null;
    this.alwaysEditable = !!options.alwaysEditable;
    this.canEdit = typeof options.canEdit === "boolean" ? options.canEdit : true;
  }

  convertFieldType(type) {
    const ft = fieldTypes.find((f) => f.type === type);
    return ft ? ft.data_type : "text";
  }

  slugify(s) {
    return String(s || "")
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  }

  isFinalState() {
    const s = String(this.currentState || "").toLowerCase();
    return [
      "approved",
      "rejected",
      "scheduled",
      "canceled",
      "cancel",
      "cancelled",
    ].includes(s);
  }
  //Rules:
  //- in_progress / reassigned just owner and reassigned
  // - in_review just reviewer can edit and resend to review
  //- approved / rejected / scheduled / canceled is bloqued just lecture
  isFormLocked() {
    if (this.alwaysEditable) return false;
    if (this.options.disabled === true) return true;

    //if canEdit is false from outside it locks
    if (!this.canEdit) return true;

    if (this.isFinalState()) return true;

    //fallback to keep compatibility if canEdit is not comming from parent we use isReviewer in in_review
    if (
      typeof this.options.canEdit === "undefined" &&
      String(this.currentState || "").toLowerCase() === "in_review"
    ) {
      return !this.isReviewer;
    }

    //other states false
    return false;
  }

  showReviewComments() {
    const s = String(this.currentState || "").toLowerCase();
    return s === "in_review" || s === "reassigned" || this.isFinalState();
  }

  reviewCommentsDisabled() {
    const s = String(this.currentState || "").toLowerCase();
    return s === "in_review" ? !this.isReviewer : true;
  }

  cfg(field) {
    const raw =
      field?.options?.fieldTypeCfg?.config || field?.options?.fieldTypeCfg || {};
    const fromArray = Array.isArray(field?.options?.fieldTypeCfg?.data)
      ? field.options.fieldTypeCfg.data.reduce((acc, it) => {
          if (it && it.key) acc[it.key] = it.default ?? null;
          return acc;
        }, {})
      : {};
    const base = { ...fromArray, ...raw };
    if (field.type === "numeric")
      return {
        decimals: Number(base.decimals ?? 0) || 0,
        min: base.min ?? null,
        max: base.max ?? null,
        allowNegative: !!base.allowNegative,
      };
    if (field.type === "minutes") return { min: base.min ?? 0, max: base.max ?? null };
    if (field.type === "time_hhmm")
      return { min: base.min ?? null, max: base.max ?? null };
    if (field.type === "duration_hm")
      return {
        minHours: base.minHours ?? 0,
        maxHours: base.maxHours ?? null,
        minMinutes: 0,
        maxMinutes: 59,
      };
    if (field.type === "duration_ms")
      return {
        minMinutes: base.minMinutes ?? 0,
        maxMinutes: base.maxMinutes ?? null,
        minSeconds: 0,
        maxSeconds: 3600,
      };
    return {};
  }

  numberStep(dec) {
    const d = Number(dec || 0);
    return d > 0 ? Number("0." + "0".repeat(d - 1) + "1") : 1;
  }

  getProcessedFields() {
    const out = [];
    const secLabelNone = this.vm.$t?.("risk.template.no_section") || "Sin sección";
    let currentSection = null,
      currentSubsection = null;
    const list = Array.isArray(this.templateFields) ? this.templateFields : [];
    for (const raw of list) {
      const f = { ...raw };
      if (typeof f.section !== "undefined") {
        currentSection = f.section || secLabelNone;
        if (typeof f.subsection !== "undefined") currentSubsection = f.subsection || null;
        if (f.type === "section_header" || f.type === "subsection_header") continue;
      }
      if (f.type === "section_header") {
        const lvl = Number((f.options && f.options.level) || 1);
        if (lvl === 1) {
          currentSection = f.name || secLabelNone;
          currentSubsection = null;
        } else {
          currentSubsection = f.name || null;
        }
        continue;
      }
      if (f.type === "subsection_header") {
        currentSubsection = f.name || null;
        continue;
      }
      if (!f.section) f.section = currentSection || secLabelNone;
      if (typeof f.subsection === "undefined") f.subsection = currentSubsection || null;
      out.push(f);
    }
    return out;
  }

  groupBySection(fields) {
    const secLabelNone = this.vm.$t?.("risk.template.no_section") || "Sin sección";
    const groups = new Map();
    const order = [];
    (fields || []).forEach((f) => {
      const key = f.section || secLabelNone;
      if (!groups.has(key)) {
        groups.set(key, { fields: [] });
        order.push(key);
      }
      groups.get(key).fields.push({ ...f });
    });
    return { order, groups, secLabelNone };
  }

  groupBySectionSubsection(fields) {
    const { order, groups } = this.groupBySection(fields);
    const structured = new Map();
    order.forEach((secName) => {
      const { fields: secFields } = groups.get(secName) || { fields: [] };
      const entry = { root: [], sub: new Map(), subOrder: [] };
      (secFields || []).forEach((f) => {
        const sub = f.subsection || null;
        if (!sub) {
          entry.root.push(f);
          return;
        }
        if (!entry.sub.has(sub)) {
          entry.sub.set(sub, []);
          entry.subOrder.push(sub);
        }
        entry.sub.get(sub).push(f);
      });
      structured.set(secName, entry);
    });
    return { order, structured, groups };
  }

  isFieldVisible(field, sectionFields, indexInSection, answers) {
    const conds = Array.isArray(field.show_if) ? field.show_if : [];
    const logic = field.show_logic === "any" ? "any" : "all";
    if (!conds.length) return true;
    const idToIndex = new Map((sectionFields || []).map((f, idx) => [f.item_id, idx]));
    const getAnsByItemId = (itemId) => {
      const idx = idToIndex.has(itemId) ? idToIndex.get(itemId) : -1;
      if (idx === -1) return { exists: false, value: undefined, idx: -1, field: null };
      const ref = sectionFields[idx];
      return {
        exists: true,
        idx,
        field: ref,
        value: answers ? answers[ref.name] : undefined,
      };
    };
    const asArray = (v) =>
      Array.isArray(v) ? v : v === undefined || v === null || v === "" ? [] : [v];
    const same = (a, b) => {
      if (Array.isArray(a) || Array.isArray(b)) {
        const aa = asArray(a).map((x) => JSON.stringify(x));
        const bb = asArray(b).map((x) => JSON.stringify(x));
        if (aa.length !== bb.length) return false;
        const setB = new Set(bb);
        return aa.every((x) => setB.has(x));
      }
      return String(a) === String(b);
    };
    const num = (v) =>
      v === "" || v === null || typeof v === "undefined" ? NaN : Number(v);
    const contains = (arr, v) =>
      (arr || []).map((x) => JSON.stringify(x)).includes(JSON.stringify(v));
    const evalOne = (c) => {
      if (!c || !c.field) return false;
      const ref = getAnsByItemId(c.field);
      if (!ref.exists) return false;
      if (ref.idx >= indexInSection) return false;
      const ans = ref.value;
      switch (c.op) {
        case "truthy":
          return !!ans;
        case "falsy":
          return !ans;
        case "==":
          return same(ans, c.value);
        case "!=":
          return !same(ans, c.value);
        case ">":
          return num(ans) > num(c.value);
        case ">=":
          return num(ans) >= num(c.value);
        case "<":
          return num(ans) < num(c.value);
        case "<=":
          return num(ans) <= num(c.value);
        case "in": {
          const arr = asArray(ans),
            vals = asArray(c.value);
          return arr.some((v) => contains(vals, v));
        }
        case "includes_all": {
          const arr = asArray(ans),
            vals = asArray(c.value);
          return vals.every((v) => contains(arr, v));
        }
        case "not_in": {
          const arr = asArray(ans),
            vals = asArray(c.value);
          return vals.every((v) => !contains(arr, v));
        }
        default:
          return false;
      }
    };
    const res = conds.map(evalOne);
    return logic === "any" ? res.some(Boolean) : res.every(Boolean);
  }

  visibleFileLabels(answersObj = {}) {
    const labels = new Set();
    const fields = this.getProcessedFields().filter((f) => {
      if (!this.options.showAlarmsOnly) return true;
      return !!(f && f.options && f.options.alarms === true);
    });
    const { order, groups } = this.groupBySection(fields);
    order.forEach((secName) => {
      const sec = groups.get(secName);
      (sec.fields || []).forEach((f, idx) => {
        if (
          f.type === "files_section" &&
          this.isFieldVisible(f, sec.fields, idx, answersObj)
        )
          labels.add(f.name);
      });
    });
    return labels;
  }

  pruneModelInvisible(model = {}) {
    const pruned = { ...(model || {}) };
    const fields = this.getProcessedFields().filter((f) => {
      if (!this.options.showAlarmsOnly) return true;
      return !!(f && f.options && f.options.alarms === true);
    });
    const { order, groups } = this.groupBySection(fields);
    order.forEach((secName) => {
      const sec = groups.get(secName);
      const secFields = sec.fields || [];
      secFields.forEach((f, idx) => {
        const visible = this.isFieldVisible(f, secFields, idx, pruned);
        if (!visible) {
          if (pruned.hasOwnProperty(f.name)) delete pruned[f.name];
          const opts = ((f.options || {}).fieldTypeCfg || {}).data || [];
          if (f.type === "select") {
            const cKey = `${f.name} - comment`;
            if (pruned.hasOwnProperty(cKey)) delete pruned[cKey];
          } else if (f.type === "checkbox") {
            opts.forEach((o) => {
              const cKey = `${f.name} - ${o.name} - comment`;
              if (pruned.hasOwnProperty(cKey)) delete pruned[cKey];
            });
          }
          if (f.type === "duration_hm") {
            const hKey = `${f.name} - horas`;
            const mKey = `${f.name} - minutos`;
            if (pruned.hasOwnProperty(hKey)) delete pruned[hKey];
            if (pruned.hasOwnProperty(mKey)) delete pruned[mKey];
          }
          if (f.type === "duration_ms") {
            const mKey = `${f.name} - minutos`;
            const sKey = `${f.name} - segundos`;
            if (pruned.hasOwnProperty(mKey)) delete pruned[mKey];
            if (pruned.hasOwnProperty(sKey)) delete pruned[sKey];
          }

          if (f.type === "files_section" && pruned.hasOwnProperty(f.name))
            delete pruned[f.name];
          return;
        }
        const opts = ((f.options || {}).fieldTypeCfg || {}).data || [];
        if (f.type === "select") {
          const sel = pruned[f.name];
          const opt = opts.find((o) => o && o.name === sel);
          const needComment = !!(opt && opt.allowComment);
          const cKey = `${f.name} - comment`;
          if (!needComment && pruned.hasOwnProperty(cKey)) delete pruned[cKey];
        } else if (f.type === "checkbox") {
          const arr = Array.isArray(pruned[f.name]) ? pruned[f.name] : [];
          const needSet = new Set(
            opts
              .filter((o) => o && o.allowComment && arr.includes(o.name))
              .map((o) => `${f.name} - ${o.name} - comment`)
          );
          opts.forEach((o) => {
            const key = `${f.name} - ${o.name} - comment`;
            if (!needSet.has(key) && pruned.hasOwnProperty(key)) delete pruned[key];
          });
        }
      });
    });
    return pruned;
  }

  makeFieldNodes(field, fieldIndex) {
    const locked = this.isFormLocked();

    if (field.type === "files_section") {
      return [
        {
          name: field.name,
          label: field.name,
          type: "component",
          component: this.customComponents.FileUploadWithPreview,
          existingFiles: this.model[field.name] || [],
          disabled: locked,
          uploading: this.options.uploadingFieldLabel === field.name,
          deleting: this.options.deletingFieldLabel === field.name,
          onUpdateFiles: this.onUpdateFiles,
        },
        { component: "hr", class: "field-divider my-4" },
      ];
    }

    if (field.type === "users" && this.customComponents.UserPickerInput) {
      return [
        {
          name: field.name,
          label: field.name,
          type: "component",
          component: this.customComponents.UserPickerInput,
          value: this.model[field.name] || null,
          disabled: locked,
          onUpdateValue: (value) => {
            if (this.vm) {
              this.vm.$set(this.vm.model, field.name, value);
            }
            this.model[field.name] = value;
            if (this.vm && this.vm._onModelTouched) {
              this.vm._onModelTouched();
            }
          },
        },
        { component: "hr", class: "field-divider my-4" },
      ];
    }

    if (field.type === "units" && this.customComponents.UnitPickerInput) {
      return [
        {
          name: field.name,
          label: field.name,
          type: "component",
          component: this.customComponents.UnitPickerInput,
          value: this.model[field.name] || null,
          disabled: locked,
          onUpdateValue: (value) => {
            if (this.vm) {
              this.vm.$set(this.vm.model, field.name, value);
            }
            this.model[field.name] = value;
            if (this.vm && this.vm._onModelTouched) {
              this.vm._onModelTouched();
            }
          },
        },
        { component: "hr", class: "field-divider my-4" },
      ];
    }

    let fieldOptions;
    if (["select", "checkbox"].includes(field.type)) {
      const rawOptions =
        field.options &&
        field.options.fieldTypeCfg &&
        Array.isArray(field.options.fieldTypeCfg.data)
          ? field.options.fieldTypeCfg.data
          : [];
      fieldOptions = rawOptions.map((o, i) => ({
        label: o?.name || `Option_${fieldIndex}_${i}`,
        value: o?.name || `Option_${fieldIndex}_${i}`,
        allowComment: !!o?.allowComment,
      }));
    }

    const mainField = {
      id: field.item_id,
      name: field.name,
      label: field.name,
      type: this.convertFieldType(field.type),
      disabled: locked,
      validation: field.options && field.options.required ? "required" : "",
      options: fieldOptions,
      attrs: {},
    };

    if (field.type === "select") {
      mainField.placeholder = this.vm.$t?.("maintenance.select") || "Select";
    }

    if (field.type === "date") {
      mainField.type = "customDate";
      mainField.fieldOptions = {
        fieldTypeCfg: { data: [{ format: { value: "d/m/Y" } }] },
      };
    }

    if (field.type === "time_hhmm") {
      const { min, max } = this.cfg(field);
      mainField.type = "time";
      mainField.attrs = {
        step: 60,
        lang: "es-ES",
        ...(min ? { min } : {}),
        ...(max ? { max } : {}),
      };
    }

    if (field.type === "numeric") {
      const { decimals, min, max, allowNegative } = this.cfg(field);
      const step = this.numberStep(decimals);
      const pattern =
        decimals > 0
          ? allowNegative
            ? "^-?\\d+(\\.\\d+)?$"
            : "^\\d+(\\.\\d+)?$"
          : allowNegative
          ? "^-?\\d+$"
          : "^\\d+$";
      mainField.type = "number";
      mainField.attrs = {
        step,
        inputmode: decimals > 0 ? "decimal" : "numeric",
        pattern,
        ...(min !== null ? { min } : {}),
        ...(max !== null ? { max } : {}),
      };
    }

    if (field.type === "minutes") {
      const { min, max } = this.cfg(field);
      mainField.type = "number";
      mainField.attrs = {
        step: 1,
        inputmode: "numeric",
        pattern: "^\\d+$",
        ...(min !== null ? { min } : { min: 0 }),
        ...(max !== null ? { max } : {}),
      };
    }

    const nodes = [];

    if (field.type === "duration_hm") {
      const { minHours, maxHours, minMinutes, maxMinutes } = this.cfg(field);

      const minutesValidation =
        `min:${minMinutes},value|max:${maxMinutes},value` +
        (field.options?.required ? "|required" : "");

      nodes.push({
        component: "div",
        class: "duration-hm-row",
        children: [
          {
            name: `${field.name} - horas`,
            label: `${field.name} (horas)`,
            type: "number",
            disabled: locked,
            validation: field.options?.required ? "required" : "",
            attrs: {
              step: 1,
              min: minHours ?? 0,
              ...(maxHours !== null ? { max: maxHours } : {}),
              inputmode: "numeric",
              pattern: "^\\d+$",
            },
          },
          {
            name: `${field.name} - minutos`,
            label: `${field.name} (minutos)`,
            type: "number",
            disabled: locked,
            validation: minutesValidation,
            attrs: {
              step: 1,
              min: minMinutes ?? 0,
              max: maxMinutes ?? 59,
              inputmode: "numeric",
              pattern: "^\\d+$",
            },
          },
        ],
      });

      nodes.push({ component: "hr", class: "field-divider my-4" });
      return nodes;
    }

    if (field.type === "duration_ms") {
      const { minMinutes, maxMinutes, minSeconds, maxSeconds } = this.cfg(field);

      const secondsValidation =
        `number|min:${minSeconds}|max:${maxSeconds}` +
        (field.options?.required ? "|required" : "");

      nodes.push({
        component: "div",
        class: "duration-hm-row",
        children: [
          {
            name: `${field.name} - minutos`,
            label: `${field.name} (minutos)`,
            type: "number",
            disabled: locked,
            validation: field.options?.required ? "required" : "",
            attrs: {
              step: 1,
              min: minMinutes ?? 0,
              ...(maxMinutes !== null ? { max: maxMinutes } : {}),
              inputmode: "numeric",
              pattern: "^\\d+$",
            },
          },
          {
            name: `${field.name} - segundos`,
            label: `${field.name} (segundos)`,
            type: "number",
            disabled: locked,
            validation: secondsValidation,
            attrs: {
              step: 1,
              min: minSeconds ?? 0,
              max: maxSeconds ?? 3600,
              inputmode: "numeric",
              pattern: "^\\d+$",
            },
          },
        ],
      });

      nodes.push({ component: "hr", class: "field-divider my-4" });
      return nodes;
    }

    if (field.type === "duration_hms") {
      const { maxHours } = this.cfg(field);

      const durationField = {
        name: field.name,
        label: `${field.name} (HH:MM:SS)`,
        type: "component",
        component: this.customComponents.DurationHmsInput,
        disabled: locked,
        maxHours: maxHours ?? 24,
        value: this.model[field.name] || { hours: 0, minutes: 0, seconds: 0 },
        onUpdateValue: (value) => {
          if (this.vm) {
            this.vm.$set(this.vm.model, field.name, value);
            this.model[field.name] = value;
            if (this.vm._onModelTouched) {
              this.vm._onModelTouched();
            }
          } else {
            this.model[field.name] = value;
          }
        },
      };

      return [durationField, { component: "hr", class: "field-divider my-4" }];
    }

    nodes.push(mainField);

    const opts = Array.isArray(fieldOptions) ? fieldOptions : [];
    if (field.type === "select") {
      const sel = this.model[field.name];
      const match = opts.find((o) => o.value === sel);
      if (match && match.allowComment) {
        nodes.push({
          name: `${field.name} - comment`,
          label: `${this.vm.$t?.("risk.comment") || "Comentario"}`,
          type: "textarea",
          disabled: locked,
        });
      }
    } else if (field.type === "checkbox") {
      const selArr = Array.isArray(this.model[field.name]) ? this.model[field.name] : [];
      opts
        .filter((o) => o.allowComment && selArr.includes(o.value))
        .forEach((o) => {
          nodes.push({
            name: `${field.name} - ${o.value} - comment`,
            label: `${field.name} - ${o.value} - ${
              this.vm.$t?.("risk.comment") || "Comentario"
            }`,
            type: "textarea",
            disabled: locked,
          });
        });
    }

    if (this.showReviewComments() && this.customComponents.ReviewCommentsWithPreview) {
      const rcKey = `review_comments_${field.name}`;
      const existingComments = this.model[rcKey] || [];
      nodes.push({
        name: rcKey,
        type: "component",
        component: this.customComponents.ReviewCommentsWithPreview,
        existingComments,
        id: rcKey,
        disabled: this.reviewCommentsDisabled(),
        onAddComment: this.onAddComment,
        onUpdateComments: ({ label, comments }) => {
          if (this.onUpdateComments) this.onUpdateComments({ label, comments });
          const allFiles = (comments || []).flatMap((c) =>
            Array.isArray(c.files) ? c.files : []
          );
          if (this.onUpdateFiles) this.onUpdateFiles({ label: rcKey, files: allFiles });
        },
      });
    }
    nodes.push({ component: "hr", class: "field-divider my-4" });
    return nodes;
  }

  convert() {
    const schema = [];
    const processed = this.getProcessedFields().filter((f) => {
      if (!this.options.showAlarmsOnly) return true;
      return !!(f && f.options && f.options.alarms === true);
    });

    const { order, groups } = this.groupBySection(processed);
    const { structured } = this.groupBySectionSubsection(processed);

    order.forEach((secName) => {
      const secFields = (groups.get(secName) || {}).fields || [];
      const indexMap = new Map(secFields.map((f, idx) => [f.item_id, idx]));

      const entry = structured.get(secName) || {
        root: [],
        sub: new Map(),
        subOrder: [],
      };

      const sectionChildren = [];
      let sectionVisibleCount = 0;

      const rootNodes = [];
      let rootVisible = 0;
      (entry.root || []).forEach((f) => {
        const idx = indexMap.get(f.item_id) ?? 0;
        if (!this.isFieldVisible(f, secFields, idx, this.model)) return;
        rootNodes.push(...this.makeFieldNodes(f, idx));
        rootVisible += 1;
      });
      if (rootVisible) {
        sectionChildren.push(...rootNodes);
        sectionVisibleCount += rootVisible;
      }

      const subOrder = entry.subOrder || [];
      subOrder.forEach((subName) => {
        const subNodes = [];
        let subVisible = 0;
        const items = entry.sub.get(subName) || [];
        items.forEach((f) => {
          const idx = indexMap.get(f.item_id) ?? 0;
          if (!this.isFieldVisible(f, secFields, idx, this.model)) return;
          subNodes.push(...this.makeFieldNodes(f, idx));
          subVisible += 1;
        });
        if (!subVisible) return;

        const cleaned =
          subNodes.length && subNodes[subNodes.length - 1].component === "hr"
            ? subNodes.slice(0, -1)
            : subNodes;

        const anchorId = `sub-${this.slugify(secName)}-${this.slugify(subName)}`;
        const cls = `rfm-anchor rfm-aid-${anchorId} rfm-sub-${this.slugify(
          secName
        )}-${this.slugify(subName)}`;
        sectionChildren.push({
          component: "div",
          class: cls,
          children: [
            {
              component: this.customComponents.SectionGroup || "div",
              title: subName,
              count: subVisible,
              initiallyCollapsed: false,
              level: 2,
              children: cleaned,
            },
          ],
        });
        sectionVisibleCount += subVisible;
      });

      if (!sectionVisibleCount) return;

      if (
        sectionChildren.length &&
        sectionChildren[sectionChildren.length - 1].component === "hr"
      ) {
        sectionChildren.pop();
      }

      const secAnchorId = `sec-${this.slugify(secName)}`;
      const clsSec = `rfm-anchor rfm-aid-${secAnchorId} rfm-sec-${this.slugify(secName)}`;
      schema.push({
        component: "div",
        class: clsSec,
        children: [
          {
            component: this.customComponents.SectionGroup || "div",
            title: secName,
            count: sectionVisibleCount,
            initiallyCollapsed: false,
            level: 1,
            children: sectionChildren,
          },
        ],
      });
    });

    return schema;
  }
}
